import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";

import type { Message, MessagePage } from "../api/types";
import type { OutboxEntry } from "../lib/outbox";
import { istLeer } from "../lib/nachricht";
import { compareMessages } from "../lib/chatOrder";
import { merkeSprung } from "../lib/tippziel";
import { jumpBeganAt, jumpBegins, jumpEnds } from "../lib/jumpState";
import { keys, type OlderAnswer, type OlderPage } from "./queries";
import { useHiddenWhileScrolling } from "./useHiddenWhileScrolling";

/**
 * How many requests one jump may make (1.102.0). Each reaches up to a
 * thousand posts (`MessagesController::JUMP_MOST`); five are five
 * thousand, more than any trip so far, and a list that long is slow in
 * the browser anyway.
 */
const JUMP_MAX_REQUESTS = 5;
/**
 * How long a landed target is also checked every frame (1.105.0), on top
 * of the observers: the pictures of the screen the chat landed on arrive
 * in this time on a phone on the road. After it, the observers alone hold.
 */
const HOLD_FRAMES_MS = 8_000;
/**
 * How long a jump waits for another load under way (1.102.0), in steps of
 * 150 ms: a minute, since `api/client.ts` ends every read after 30 s.
 */
const WAIT_STEPS = 400;
const WAIT_STEP_MS = 150;

/**
 * Where the thread jumps, and what counts as read.
 *
 * Pulled out of `ThreadPage.tsx` in 1.23.0, which had eleven effects; six of
 * them belonged together and are here: the jump to the newest end after
 * sending, the jump to the first unread post, the search backwards for the
 * edge where reading stopped, the one jump per `?m=`, the reset on switching
 * threads, and the report that the thread was read. What they have in common
 * is a scroll box and the question "once or again?" -- and each answer used
 * to sit next to a different one.
 *
 * The page keeps the list, the dividers and the menu.
 */
export function useVerlaufsprung({
  tripId,
  withUser,
  entwurf,
  messages,
  older,
  markRead,
  wartende,
  filter,
}: {
  tripId: number;
  withUser: number | undefined;
  /** Der Chatentwurf (#85): dort gibt es nichts zu lesen und nichts zu melden. */
  entwurf: boolean;
  /** Der geladene Verlauf -- im Entwurf die Entwurfsliste, sonst der Chat. */
  messages: UseQueryResult<MessagePage>;
  /** Ältere Seiten nachladen -- für die Kante und für einen Sprung ins Alte. */
  older: UseMutationResult<OlderAnswer, Error, OlderPage>;
  markRead: UseMutationResult<unknown, Error, string>;
  /** Was noch im Ausgangskorb wartet -- der Sprung ans Ende hält, bis es leer ist. */
  wartende: OutboxEntry[];
  /**
   * The filter of the chat (1.93.0, #114). `sichtbar` leaves the hidden rows
   * out of the unread edge -- the "Neu" line stands at the first unread row
   * one can see. `istVersteckt` and `aufheben` let a jump reach a hidden
   * row: it lifts the filter and jumps after the next draw.
   */
  filter?: {
    sichtbar: (m: Message) => boolean;
    istVersteckt: (messageId: number) => boolean;
    aufheben: () => void;
  };
}) {
  /**
   * Der Verlauf selbst -- gebraucht wird er nur zum Springen ans Ende.
   *
   * **Aus dem Betrieb:** „wenn der Bildschirm schon voll ist, oder man nach
   * oben gescrollt hat: die letzte abgesendete Nachricht ist nach dem
   * Absenden nicht sichtbar, weil die Nachrichten nicht nach oben scrollen."
   *
   * Getragen hat das bisher allein `flex-direction: column-reverse`: solange
   * man **unten** steht, hängt der Browser von selbst am neuesten Ende. Wer
   * hochgescrollt hat, steht aber nicht mehr unten -- und dann ist das
   * richtige Verhalten für eine *fremde* Nachricht (bleiben, wo man liest)
   * das falsche für die *eigene*. Deshalb springt nur das Senden.
   */
  const verlauf = useRef<HTMLDivElement>(null);
  const client = useQueryClient();
  // The floating switcher steps aside while the thread scrolls (0.46.0).
  const verlaufScrollt = useHiddenWhileScrolling(verlauf);

  /**
   * Ans neueste Ende springen.
   *
   * `scrollTop = 0` ist in einer umgekehrten Liste das **untere** Ende; nach
   * oben läuft die Zahl ins Minus.
   *
   * **Ohne Weichzeichnen, und das ist gemessen.** Mit `behavior: "smooth"`
   * schrieb eine Spur alle 50 ms dies mit:
   *
   * ```
   * -607 -599 -556 -474 -400 -233 -156 -65 -14 -56 -56 -56 …
   * ```
   *
   * Der weiche Lauf kommt bis −14 und wird dann auf −56 zurückgerissen: die
   * neue Blase trifft erst mit der Antwort des Servers ein, die
   * Scrollverankerung schiebt um deren Höhe -- und eine laufende Animation
   * verliert gegen diese Verschiebung. Ein gesetzter Wert verliert nicht.
   */
  function springen() {
    const kasten = verlauf.current;
    if (kasten !== null) kasten.scrollTop = 0;
  }

  /**
   * Ein Sprung ist gewünscht -- aber einer reicht nicht.
   *
   * **Gemessen, nicht vermutet:** ein Sprung allein beim Absenden landete
   * daneben, um genau eine Blasenhöhe (Versatz −55 bei einer Zeile von 53).
   * Der Grund ist die Scrollverankerung des Browsers: die neue Blase kommt
   * *nach* dem Sprung dazu, und der Browser hält den sichtbaren Inhalt an
   * seinem Platz, indem er den Versatz um deren Höhe nachzieht. Der Sprung war
   * also richtig und wurde danach zurückgedreht.
   *
   * Deshalb wird der Wunsch gemerkt und **nach dem Zeichnen** noch einmal
   * gesprungen. Er hält, bis nichts mehr im Ausgangskorb wartet: ohne Netz
   * steht dort erst ein Eintrag, der später gegen die echte Nachricht getauscht
   * wird -- und dieser Tausch ist wieder ein Zeichnen.
   */
  const willAnsEnde = useRef(false);

  function nachUntenSpringen() {
    releaseHold.current?.();
    willAnsEnde.current = true;
    springen();
  }

  // **Die Zahl der Nachrichten, nicht die gefilterte Liste.** Die steht erst
  // weit unten, hinter den frühen Rückgaben -- ein Hook darf dort nicht mehr
  // stehen. Für „es ist etwas dazugekommen" tut es diese Zahl genauso.
  const anzahl = messages.data?.messages.length ?? 0;

  useEffect(() => {
    if (!willAnsEnde.current) return;
    springen();
    // Erst wenn nichts mehr wartet, ist der Vorgang vorbei.
    if (wartende.length === 0) willAnsEnde.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anzahl, wartende.length]);

  /** Which chat a jump belongs to -- counted up on every switch and on leaving (1.112.0). */
  const jumpRun = useRef(0);

  /**
   * Where a jump spends its time (1.195.1). From use, with a recording: a
   * jump from the search stood fourteen seconds behind "Der Beitrag wird
   * geladen …", and nothing said whether the server, the line or the
   * drawing took them. Two lines in the diagnosis: the answer (rows, size,
   * waiting for the server, loading), and the landing (after the answer and
   * after the tap). A tool for this one question; it goes when it is
   * answered (CLAUDE.md, #167).
   */
  const jumpClock = useRef<{ id: number; answer: number | null } | null>(null);

  // Beim Wechsel des Chats ist der Wunsch hinfällig -- sonst spränge der neue
  // Verlauf, weil im alten etwas gesendet wurde.
  //
  // **And a jump under way is over** (1.112.0, review). `springeZu()` goes
  // on after every answer and every wait through timers nobody cleared; it
  // read the cursor from the cache of the chat just left -- trimmed on
  // leaving -- and loaded up to five thousand rows into a closed chat, with
  // the sign on the trip's page. A chat switched without unmount
  // (`ThreadPage` stays, only the address moves) was worse: `older` is the
  // hook of the new chat, with `before` and `until` of the old. The run
  // number below says which chat a step belongs to; a step from an earlier
  // run returns at once.
  //
  // **`entwurf` is part of the switch** (1.112.0, review): the draft is a
  // different list in the same component, and the sign "Der Chat wird
  // geladen …" from the link to it stood its full 45 s because nothing
  // ended it.
  useEffect(() => {
    willAnsEnde.current = false;
    // Dasselbe für den Lesestand und den einen Sprung: ein anderer Verlauf
    // ist ein neues Öffnen, auch wenn das Bauteil stehen bleibt (#35).
    standBeimOeffnen.current = null;
    gesprungen.current = false;
    // Auch die Suche nach der Kante beginnt von vorn -- sie gehört dem Öffnen.
    kanteSeiten.current = 0;
    setKanteAufgegeben(false);
    releaseHold.current?.();
    jumpRun.current += 1;
    setLoadingTo(null);
  }, [tripId, withUser, entwurf]);

  // Leaving the page lets go of a held target too, and ends a jump that has
  // not landed: the sign belongs to the chat it was going to.
  useEffect(() => () => {
    releaseHold.current?.();
    jumpRun.current += 1;
    jumpEnds();
  }, []);

  /**
   * **A target that was jumped to stays where it landed** (1.102.0), until
   * the reader touches the chat.
   *
   * From use, with a screen recording: the jump from the list of polls
   * landed two to three screens below the poll, and stayed there. The
   * chat is drawn upside down (`column-reverse`): its scroll position
   * counts from the newest end, so whatever grows *below* the target --
   * new posts from the tick, a picture that arrives -- pushes the view
   * towards the newer posts. Chromium keeps a scroll anchor and holds the
   * view; Safari got one only very lately, and the device showed
   * exactly that drift. Measured with the anchor off (`sprungziel.mjs`):
   * six posts from the tick moved the poll 2288 points out of the middle.
   *
   * So the target is held by hand: every change of size in the chat and
   * every new row puts it back where it landed. That is a measurement of
   * where it stands, not a correction by a guessed amount -- where the
   * browser anchors on its own, the target has not moved and nothing is
   * done. It ends with the first touch, wheel or key, with the next jump,
   * with sending (`nachUntenSpringen`) and with leaving the chat: from
   * then on the reader decides where the chat stands.
   */
  const releaseHold = useRef<((event?: Event | string) => void) | null>(null);

  function holdTarget(messageId: number) {
    releaseHold.current?.();
    const box = verlauf.current;
    if (box === null || document.getElementById(`msg-${messageId}`) === null) return;
    const offset = () => {
      const element = document.getElementById(`msg-${messageId}`);
      return element === null ? null : element.getBoundingClientRect().top - box.getBoundingClientRect().top;
    };
    const wanted = offset();
    if (wanted === null) return;
    let corrected = 0;
    // **Someone else scrolled: let go** (1.105.0). Growth below the target
    // moves the target and leaves the scroll position as it is -- the chat
    // is drawn upside down and counts from its newest end. A scroll by
    // anyone else moves the position itself: the reader through a way that
    // is not a touch (the tap on iOS's status bar scrolls to the top), or
    // code. Until 1.104.0 only a touch, a wheel or a key let go; with the
    // check every frame below, a scroll of that kind was taken back for
    // eight seconds -- measured with `aenderungsmodus.mjs`, whose run
    // scrolls to the newest post and then pressed beside it.
    let ours = box.scrollTop;
    // **A scroll by the browser itself is not the reader's** (1.117.0). From
    // the iPad, in the diagnosis: "gelandet bei 280", 48 ms later "beendet:
    // Scrollen, 0 Korrekturen" -- Safari moved `scrollTop` right after
    // `scrollIntoView` on its own (rounding to device pixels, its own
    // anchoring, a late adjustment in the upside-down list), the hold took it
    // for the reader and let go, and the pictures below pushed the target
    // away. So the question is asked at the target and not at `scrollTop`:
    // the reader's scroll (the tap on the status bar goes to the top) moves
    // it far; whatever moves it less than a third of the box is taken over
    // and corrected like growth. A touch, a wheel or a key still let go at
    // once (`INPUTS` below) -- that stays the main rule.
    let foreign = 0;
    // **Once stopped, nothing more** (1.111.0). The check every frame below
    // called `stop()` from inside the frame and then asked for the next one
    // -- the cancel in `stop()` had nothing left to cancel. The hold went on
    // for its eight seconds and ended again in every frame, a line each
    // time: sixty a second pushed everything else out of the thirty lines
    // of the record (measured with `pushkarte.mjs`, whose "Angetippt" lines
    // were gone).
    let stopped = false;
    const putBack = (how: string) => {
      if (stopped) return;
      const now = offset();
      if (now === null) return;
      const drift = now - wanted;
      if (Math.abs(box.scrollTop - ours) >= 1) {
        if (Math.abs(drift) > box.clientHeight / 3) {
          stop(`Scrollen (scrollTop ${Math.round(ours)} → ${Math.round(box.scrollTop)}, Ziel ${Math.round(drift)} daneben)`);
          return;
        }
        foreign += 1;
        if (foreign <= 6) merkeSprung(`Halten m=${messageId} (${how}): scrollTop vom Browser ${Math.round(ours)} → ${Math.round(box.scrollTop)} übernommen, Ziel ${Math.round(drift)} daneben`);
        ours = box.scrollTop;
      }
      if (Math.abs(drift) < 1) return;
      const before = box.scrollTop;
      box.scrollTop += drift;
      ours = box.scrollTop;
      // **Every correction goes into the record** (1.105.0). From use, on
      // the iPad: a picture below the target arrived a second after the
      // landing, and the target went up by exactly its height -- as if
      // nothing held it. Chromium in the test bed holds it; what Safari
      // does is only to be seen on the device, so the numbers are written
      // where the device shows them (Einstellungen › Diagnose). The first
      // few of a jump are enough to tell.
      corrected += 1;
      if (corrected <= 6) {
        const after = offset();
        merkeSprung(`Halten m=${messageId} (${how}): ${Math.round(drift)} verrutscht, scrollTop ${Math.round(before)} → ${Math.round(box.scrollTop)}, danach ${after === null ? "weg" : Math.round(after - wanted)}`);
      }
    };
    const sizes = new ResizeObserver(() => putBack("Größe"));
    const watch = (element: Element) => sizes.observe(element);
    Array.from(box.children).forEach(watch);
    const rows = new MutationObserver((changes) => {
      for (const change of changes) change.addedNodes.forEach((node) => {
        if (node instanceof Element) watch(node);
      });
      putBack("Zeile");
    });
    rows.observe(box, { childList: true });
    // **And once per frame for a while, as a backstop** (1.105.0). The two
    // observers above are the measure; if Safari misses a change of size
    // somewhere below the target, this still sees where the target stands.
    // Only for the first seconds, while the pictures of the screen arrive:
    // a check every frame for as long as someone reads would cost battery
    // for nothing.
    const startedAt = performance.now();
    let frame = 0;
    const everyFrame = () => {
      putBack("Bild");
      if (!stopped && performance.now() - startedAt < HOLD_FRAMES_MS) frame = requestAnimationFrame(everyFrame);
    };
    frame = requestAnimationFrame(everyFrame);
    const INPUTS = ["pointerdown", "touchstart", "wheel", "keydown"] as const;
    const stop = (event?: Event | string) => {
      if (stopped) return;
      stopped = true;
      sizes.disconnect();
      rows.disconnect();
      cancelAnimationFrame(frame);
      INPUTS.forEach((kind) => window.removeEventListener(kind, stop, true));
      if (releaseHold.current === stop) releaseHold.current = null;
      // Why it let go: a touch of the reader, or the program (next jump,
      // sending, leaving). An event that is not `isTrusted` was made by
      // code -- that would be a bug of ours.
      const why = event === undefined ? "vom Programm"
        : typeof event === "string" ? event
          : `${event.type}${event.isTrusted ? "" : " (nicht vom Menschen)"}`;
      merkeSprung(`Halten m=${messageId} beendet: ${why}, ${corrected} Korrekturen`);
    };
    INPUTS.forEach((kind) => window.addEventListener(kind, stop, { capture: true, passive: true }));
    releaseHold.current = stop;
    merkeSprung(`Halten m=${messageId}: gelandet bei ${Math.round(wanted)} vom oberen Rand`);
  }

  /** The line about the answer of a jump's request, from the browser's own timing of it. */
  function noteAnswer(messageId: number, page: MessagePage) {
    const now = performance.now();
    if (jumpClock.current?.id === messageId) jumpClock.current.answer = now;
    const entry = performance
      .getEntriesByType("resource")
      .filter((e) => e.name.includes("/messages?") && e.name.includes(`until=${messageId}`))
      .at(-1) as PerformanceResourceTiming | undefined;
    const kb = Math.round(JSON.stringify(page).length / 1024);
    const rows = page.messages.length;
    if (entry === undefined || entry.responseStart === 0) {
      merkeSprung(`Sprung zu m=${messageId}: Antwort ${rows} Zeilen, ${kb} KB, ohne Zeitmessung`);
      return;
    }
    const wire = entry.encodedBodySize > 0 ? `, übertragen ${Math.round(entry.encodedBodySize / 1024)} KB` : "";
    merkeSprung(
      `Sprung zu m=${messageId}: Antwort ${rows} Zeilen, ${kb} KB${wire}, Anfrage ${Math.round(entry.duration)} ms ` +
      `(Warten auf den Server ${Math.round(entry.responseStart - entry.requestStart)} ms, Laden ${Math.round(entry.responseEnd - entry.responseStart)} ms)`,
    );
  }

  /**
   * Zur bezogenen Nachricht springen.
   *
   * Steht sie schon im Verlauf, geht es sofort dorthin, und sie leuchtet kurz
   * auf -- ohne das weiß niemand, wo man gelandet ist.
   *
   * Steht sie nicht da, wird nachgeladen, und zwar **bis zu ihr in einer
   * Anfrage** (`until`, 1.102.0).
   *
   * **Every jump comes through here, the same way** (1.102.0). From use:
   * "idealerweise ein Code für alle Sprünge". Six ways reach this function
   * through `?m=` -- polls, day separators, days, photos, search,
   * notification -- and the quote calls it directly. The quote alone used
   * to load one page and stop ("sich seitenweise rückwärts durch einen
   * Sommer zu laden wäre kein Sprung mehr"); that was an argument about
   * pages, and with one request there are none. `JUMP_MAX_REQUESTS`
   * caps it: each request reaches up to a thousand posts.
   */
  function springeZu(messageId: number, requestsLeft = JUMP_MAX_REQUESTS, weiter?: boolean, waited = 0, run = jumpRun.current) {
    // A step of a jump from a chat since left or switched (1.112.0).
    if (run !== jumpRun.current) return;
    // A jump ends the search for the unread edge (1.102.0): it would go on
    // paging backwards, and this jump would wait for every page of it.
    gesprungen.current = true;
    // **A jump to a hidden row lifts the filter** (1.93.0, #114, decided
    // from use). The row is loaded but not drawn, so the search below would
    // take it for an older one and page backwards through the whole trip.
    // The notification, the quote, the list of days -- all come through here.
    if (filter?.istVersteckt(messageId) === true) {
      filter.aufheben();
      window.setTimeout(() => springeZu(messageId, requestsLeft, weiter, waited, run), 60);
      return;
    }
    // A fresh call (not a step after an answer or a wait) starts the clock.
    const fresh = weiter === undefined && waited === 0 && requestsLeft === JUMP_MAX_REQUESTS;
    if (fresh || jumpClock.current?.id !== messageId) jumpClock.current = { id: messageId, answer: null };
    const ziel = document.getElementById(`msg-${messageId}`);
    if (ziel !== null) {
      const clock = jumpClock.current;
      if (clock.answer !== null) {
        const now = performance.now();
        const tap = jumpBeganAt();
        merkeSprung(`Sprung zu m=${messageId}: im Bild ${Math.round(now - clock.answer)} ms nach der Antwort${tap === null ? "" : `, ${Math.round(now - tap)} ms nach dem Tipp`}`);
      }
      jumpClock.current = null;
      // **At once, not smooth** (1.102.0). A smooth run over many screens
      // goes past posts whose pictures arrive on the way, and the layout
      // moves under it; where it then stops is not where it was sent. The
      // flash below shows where the chat landed.
      ziel.scrollIntoView({ block: "center" });
      holdTarget(messageId);
      setLoadingTo(null);
      ziel.classList.add("bubble-row--gefunden");
      window.setTimeout(
        () => ziel.classList.remove("bubble-row--gefunden"),
        1600,
      );
      return;
    }

    // **Der Cursor kommt aus dem Verlauf, wie er jetzt steht** (1.93.1) --
    // nicht aus dem Zeichnen und nicht mehr aus der letzten Antwort.
    // Diese Funktion ist die vom Zeichnen, in dem der Sprung begann;
    // `messages.data` darin ist der Stand von damals. Beim zweiten Schritt
    // stünde dort derselbe Cursor wie beim ersten -- gemessen: dieselbe
    // Seite zweimal geladen, und die gesuchte nie erreicht. Bis 1.93.0 kam
    // er deshalb aus der Antwort des vorigen Schritts. Seit eine Seite, die
    // nicht ans Ende passt, verworfen wird (`useOlderMessages`), wäre das
    // eine Sackgasse: jeder weitere Schritt fragte hinter dem Ende an, und
    // jede Antwort fiele wieder weg -- gemessen, der Sprung kam nicht an
    // (`loch.mjs`, nur diese Sperre aktiv). Der Zwischenspeicher kennt
    // beides nicht: er ist immer der Stand von jetzt. **Ein Loch verhindert
    // diese Zeile allein nicht** -- auch das gemessen: ein Schritt kann
    // abgehen, bevor der Takt die Seite davor wegschreibt.
    const stand = client.getQueryData<MessagePage>(keys.messages(tripId, withUser)) ?? messages.data;
    const cursor = stand?.next_before ?? null;
    // **Loaded but not yet drawn: wait for the draw, do not load further**
    // (1.112.0). The answer to `until` is in the cache a tick before React
    // has its rows in the DOM.
    const rows = stand?.messages ?? [];
    if (rows.some((m) => m.id === messageId)) {
      if (waited < WAIT_STEPS) window.setTimeout(() => springeZu(messageId, requestsLeft, weiter, waited + 1, run), WAIT_STEP_MS);
      else {
        setLoadingTo(null);
        merkeSprung(`Sprung zu m=${messageId}: geladen, aber nie gezeichnet, aufgegeben`);
      }
      return;
    }
    // **Past the target without it: it is not this reader's** (1.112.0,
    // review). The server answers down to `until` with what the reader may
    // see; a deleted post, or one behind a cutoff, never comes. Before,
    // the jump asked five more times for a thousand rows each -- on shared
    // hosting, with pictures, polls and reactions on every row -- for a
    // target that could not come.
    //
    // **Past by place, not by id** (#130). An inserted post has a high id
    // and an old place: by id every loaded row was "older" than it, and the
    // jump gave up after the first answer whether or not it reached. The
    // server says where the target stands (`until_key`), and the oldest
    // loaded row is compared with that. No key at all means the target is
    // no row of this trip.
    const oldest = rows[rows.length - 1];
    const target = targetPlace.current?.id === messageId ? targetPlace.current : null;
    if (weiter === true && target !== null && (target.key === null || (oldest !== undefined && compareMessages(oldest, { id: messageId, sort_key: target.key }) < 0))) {
      setLoadingTo(null);
      merkeSprung(`Sprung zu m=${messageId}: nicht im Verlauf dieses Lesers (älteste geladene ${oldest?.id ?? "keine"} an Platz ${oldest?.sort_key ?? "?"}, Ziel an Platz ${target.key ?? "keinem"}), aufgegeben`);
      return;
    }
    // **Another load under way is waited for, not a reason to stop**
    // (1.102.0). Here stood a bare `return`: a quote tapped while the
    // search for the unread edge loaded did nothing at all. The bar stands
    // while it waits. `olderNow` is the hook of the latest draw -- this
    // function is the one of the draw the jump began in.
    if (weiter !== true && olderNow.current.isPending) {
      setLoadingTo(messageId);
      if (waited < WAIT_STEPS) window.setTimeout(() => springeZu(messageId, requestsLeft, false, waited + 1, run), WAIT_STEP_MS);
      else {
        setLoadingTo(null);
        merkeSprung(`Sprung zu m=${messageId}: andere Ladung endete nicht, aufgegeben`);
      }
      return;
    }
    if (requestsLeft <= 0 || cursor === null) {
      setLoadingTo(null);
      // **Keine stille Absage**, auch nicht in der Diagnose: bis 1.93.0 stand
      // dort „lade zurück" und danach nichts, gleich ob der Sprung ankam.
      if (weiter === true && cursor === null) merkeSprung(`Sprung zu m=${messageId}: nicht im Verlauf, Anfang erreicht`);
      else if (weiter === true) merkeSprung(`Sprung zu m=${messageId}: nicht im Verlauf, Grenze erreicht`);
      return;
    }
    setLoadingTo(messageId);
    // `mutateAsync` und nicht die Rückrufe von `mutate`: die eines Aufrufs
    // fallen weg, sobald ein anderer Weg (Kante, Knopf, Filter) dazwischen
    // ältere Seiten lädt -- und der Sprung hörte still auf.
    // The jump names its target, and the server answers down to it in one
    // request (1.102.0).
    older.mutateAsync({ before: cursor, until: messageId }).then(
      // Erst nach dem Zeichnen suchen: vorher gibt es das Element noch nicht.
      ({ page }) => {
        targetPlace.current = { id: messageId, key: page.until_key ?? null };
        if (run === jumpRun.current) noteAnswer(messageId, page);
        window.setTimeout(() => springeZu(messageId, requestsLeft - 1, true, 0, run), 60);
      },
      (fehler: unknown) => {
        if (run !== jumpRun.current) return;
        setLoadingTo(null);
        merkeSprung(`Sprung zu m=${messageId}: ältere Seite fehlgeschlagen (${fehler instanceof Error ? fehler.message : String(fehler)})`);
      },
    );
  }

  /**
   * The post a jump is loading towards, for the bar over the chat
   * (1.102.0). From use: "einen Fortschrittsbalken während nachgeladen
   * wird". Set when a jump has to ask the server -- for older posts, or
   * for the newest list when the post rang just now -- and cleared when it
   * lands, gives up or fails. A request cannot tell how far it is, so the
   * bar runs (`LoadingBar`) instead of filling up.
   */
  //
  // **Since 1.105.0 the flag lives in `lib/jumpState.ts`** and `Shell`
  // draws the sign: a jump from another page is marked at the tap, while
  // React still shows the old page (see there). The chat ends it when it
  // lands or gives up, as before. Nothing here reads the flag (1.112.0,
  // review): a subscription drew the whole chat anew on every begin and
  // end -- with the thousand rows a jump had just loaded.
  const setLoadingTo = (to: number | null) => (to === null ? jumpEnds() : jumpBegins());
  const olderNow = useRef(older);
  olderNow.current = older;
  /** Where the jump's target stands, from the server's answer to `until` (#130). */
  const targetPlace = useRef<{ id: number; key: number | null } | null>(null);

  /**
   * Wie weit beim **Öffnen** gelesen war (#35).
   *
   * Aus dem Betrieb: „Bei Abrufen des Chats an die Stelle der Nachrichten
   * blättern, die nach der letzten Online-Zeit waren [...] wenn der Benutzer
   * schon aktiv ist, nicht selbständig scrollen."
   *
   * **Die Frage „wie erkennen wir ein neues Aufrufen?" beantwortet das
   * Bauteil selbst:** ein Öffnen ist ein Aufbau. Wer im Chat bleibt, baut
   * nichts neu auf, und dieser Merker füllt sich genau einmal.
   *
   * **Er ist vor dem Melden gefüllt, und zwar von selbst.** Der Lesestand wird
   * beim Öffnen sofort fortgeschrieben; käme die Meldung zuerst, gäbe es nie
   * eine ungelesene Nachricht zu sehen. Hier stand deshalb eine Sperre am
   * Melden -- **die Gegenprobe hat ihn widerlegt**: gemerkt wird beim
   * *Zeichnen*, gemeldet in einem *Effekt*, und Effekte laufen nach dem
   * Zeichnen. Die Reihenfolge liegt in der Bauart, nicht in einer Bedingung.
   * Eine Sperre, den nichts braucht, sagt dem nächsten Leser das Gegenteil.
   *
   * **Genommen wird der erste Wert, den dieser Aufbau zu sehen bekommt** --
   * auch der aus dem Zwischenspeicher. Hier stand `isFetchedAfterMount`, mit
   * der Überlegung, aus dem Speicher käme ein zu alter Stand; gemessen war
   * das Gegenteil: bei einem Klick aus der Liste liegt der Verlauf im Speicher,
   * die Meldung des Lesens ging **vor** der ersten frischen Antwort hinaus,
   * und der Merker blieb leer -- keine Trennlinie, obwohl drei Nachrichten
   * ungelesen waren.
   *
   * Der gespeicherte Stand ist dabei kein schlechterer: er ist die Antwort
   * vom letzten Öffnen desselben Verlaufs, also genau das, was damals gelesen
   * war. Zu *hoch* kann er nicht sein.
   */
  const standBeimOeffnen = useRef<number | null>(null);
  if (standBeimOeffnen.current === null && messages.data !== undefined) {
    standBeimOeffnen.current = messages.data.last_read_id;
  }

  /**
   * Die erste ungelesene Nachricht — oder `null`.
   *
   * `list` läuft von neu nach alt; die *erste* ungelesene im Lesesinn ist
   * also die **letzte** ungelesene in dieser Reihenfolge. Gesucht wird die
   * Kante: eine ungelesene, deren älterer Nachbar schon gelesen war.
   *
   * **Ohne sichtbare Kante keine Linie.** Ist die älteste geladene selbst
   * ungelesen und gibt es noch ältere zum Nachladen, wissen wir nicht, wo der
   * Verlauf umschlägt — eine Linie stünde dann irgendwo mitten im Ungelesenen
   * und behauptete eine Grenze, die woanders liegt. Nur wenn es gar nichts
   * Älteres mehr gibt, ist die älteste geladene wirklich die erste.
   *
   * **Daraus wurde aber „dann eben gar nichts", und das war der Fehler.** Aus
   * dem Betrieb: „wenn neue Nachrichten in einem älteren Abschnitt sind wird
   * nicht dorthin gesprungen, sondern ans Ende und man muss ältere Nachrichten
   * manuell nachladen." Genau dieser Fall — mehr Ungelesenes, als auf eine
   * Seite passt. Die Kante liegt dann nicht nirgends, sie liegt **weiter
   * oben**; gesucht wird sie jetzt dort (`kanteFehlt` weiter unten), statt
   * aufzugeben.
   *
   * Fremde Nachrichten zählen, eigene nicht: was man selbst geschrieben hat,
   * hat man nicht zu lesen versäumt.
   */
  const ersteUngelesene = (() => {
    const bis = standBeimOeffnen.current;
    if (bis === null) return null;
    // **Aus `messages.data`, nicht aus `list`.** Die gefilterte Liste steht
    // erst hinter den frühen Rückgaben, und ein Hook darf dort nicht mehr
    // stehen -- dieselbe Überlegung wie bei `anzahl` weiter oben. Gelöschte
    // werden hier gleich mit übersprungen; sie stehen im Verlauf nicht.
    // **An inserted post is never new** (#130): it rings for nobody and
    // counts for nobody, so it draws no line either. Without it the rest
    // stands in the order it was written, and the ids below compare.
    const alle = (messages.data?.messages ?? []).filter((m) => !m.deleted && !m.inserted && !istLeer(m) && (filter?.sichtbar(m) ?? true));
    const nochAeltere = messages.data?.next_before != null;
    for (let i = 0; i < alle.length; i++) {
      const m = alle[i];
      if (m === undefined || m.own || m.id <= bis) continue;
      const aelter = alle[i + 1];
      if (aelter !== undefined && aelter.id <= bis) return m.id;
      if (aelter === undefined && !nochAeltere) return m.id;
    }
    return null;
  })();

  /**
   * **Die Kante liegt weiter oben, nicht nirgends.**
   *
   * Wahr, solange die älteste geladene Nachricht selbst ungelesen ist und es
   * noch ältere gibt: dann ist der Umschlagpunkt eine Seite weiter oben, und
   * `ersteUngelesene` kann ihn von hier aus nicht sehen.
   */
  const kanteFehlt = (() => {
    const bis = standBeimOeffnen.current;
    if (bis === null || ersteUngelesene !== null) return false;
    if (messages.data?.next_before == null) return false;
    const alle = (messages.data?.messages ?? []).filter((m) => !m.deleted && !m.inserted && !istLeer(m) && (filter?.sichtbar(m) ?? true));
    const aelteste = alle[alle.length - 1];
    return aelteste !== undefined && !aelteste.own && aelteste.id > bis;
  })();

  /**
   * Wie weit rückwärts gesucht wird, bevor es dabei bleibt.
   *
   * Fünf Seiten, dieselbe Zahl und derselbe Grund wie `LUECKE_MAX_SEITEN` in
   * `hooks/queries.ts`: wer mehr als zweihundert Nachrichten ungelesen hat,
   * war nicht kurz weg. Ohne Grenze wäre es eine Schleife über den ganzen
   * Verlauf — und die liefe ausgerechnet dann lange, wenn ohnehin viel
   * nachzuladen ist.
   *
   * **Wird die Grenze erreicht, bleibt der Verlauf stehen, wo er steht**, und
   * sagt es über dem Knopf. Stillschweigend ans Ende zu springen war der alte
   * Zustand und war der Befund.
   */
  const KANTE_MAX_SEITEN = 5;
  const kanteSeiten = useRef(0);
  const [kanteAufgegeben, setKanteAufgegeben] = useState(false);

  useEffect(() => {
    if (!kanteFehlt || gesprungen.current || older.isPending) return;
    const cursor = messages.data?.next_before ?? null;
    if (cursor === null) return;
    if (kanteSeiten.current >= KANTE_MAX_SEITEN) {
      setKanteAufgegeben(true);
      return;
    }
    kanteSeiten.current += 1;
    older.mutate(cursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kanteFehlt, older.isPending, messages.data?.next_before]);

  /**
   * Gesprungen wird **einmal** je Öffnen.
   *
   * Ein `useRef` und kein Zustand: es wird nirgends gezeichnet, und ein
   * erneutes Zeichnen wegen einer neu eintreffenden Nachricht darf den Sprung
   * nicht wiederholen -- sonst risse es einem den Verlauf unter der Hand weg,
   * während man liest. Genau das steht im Issue: „wenn der Benutzer schon
   * aktiv ist, nicht selbständig scrollen."
   */
  const gesprungen = useRef(false);
  /**
   * The post a notification named (`?m=`) -- read here already, because the
   * jump to the first unread gives way to it (1.76.0): two jumps in one
   * opening, one instant and one smooth, ended wherever the second lost to
   * the first.
   */
  const ort = useLocation();
  const gesucht = Number(new URLSearchParams(ort.search).get("m") ?? "");
  const mitZiel = Number.isInteger(gesucht) && gesucht > 0;
  useEffect(() => {
    if (gesprungen.current || ersteUngelesene === null || mitZiel) return;
    const ziel = document.getElementById(`msg-${ersteUngelesene}`);
    if (ziel === null) return;
    gesprungen.current = true;
    ziel.scrollIntoView({ block: "center" });
  }, [ersteUngelesene]);

  /**
   * **`?m=` — der Beitrag, der geklingelt hat.**
   *
   * Aus dem Betrieb: „Klick auf die Benachrichtigung führt aber nicht zur
   * Abstimmung oder zumindest zum richtigen Chat. → das sollte generell bei
   * Benachrichtigung funktionieren, dass man im richtigen Chat bzw. der
   * Nachricht landet."
   *
   * Der Chat allein ist die halbe Auskunft: wer nach zwanzig Beiträgen
   * hineinkommt, sucht den, der geklingelt hat. `springeZu()` kann das längst
   * — samt Nachladen, wenn der Beitrag noch nicht geladen ist; ihm fehlte nur
   * die Nummer.
   *
   * **Einmal je Aufbau, und danach nie wieder.** Ein zweiter Sprung risse
   * einem den Verlauf unter der Hand weg — dieselbe Regel wie beim Sprung zur
   * ersten ungelesenen Nachricht darüber. Und er wartet, bis die Liste
   * wirklich da ist: vorher gibt es das Element nicht, und `springeZu()`
   * lüde eine Seite nach, die schon unterwegs ist.
   */
  // **Once per number, not once per mount** (1.19.0): the day sheet hands
  // a new `?m=` to the same, still mounted page, and that one must jump
  // too. What stays is: never twice to the same one.
  //
  // **And it waits for a list that can know the post** (1.76.0). From use:
  // "die Benachrichtigungen führen nicht direkt zur Nachricht" -- every kind,
  // and differently depending on whether the app was open. The jump ran as
  // soon as *any* list was there, and on a tap that is the stored one
  // (`lib/vorrat.ts`, the query cache): it does not know the post that just
  // rang. `springeZu()` then searched **backwards** through older pages for
  // a post newer than everything loaded -- in a short chat it gave up at
  // once, in a long one it depended on the poll -- and the mark was set, so
  // the fresh list never tried again.
  //
  // So: a post newer than the newest loaded one asks for the list once
  // (`refetch`) and waits for it; only an older one goes backwards. The mark
  // is set when the jump happens or is given up, and both go into the
  // record under Einstellungen › Diagnose -- "gesprungen nach …" alone said
  // nothing about whether the post was reached.
  //
  // **Once per navigation, not once per number** (1.87.3). From use: "nicht
  // immer innerhalb eines Chats, wenn es nur einen Tagestrenner gibt" --
  // with one separator every choice in the list is the same post, and the
  // mark "never twice to the same one" turned the second tap into nothing.
  // The mark is now the navigation (`location.key`, new with every
  // `navigate()`, also to the same address): a redraw of the same
  // navigation still never jumps twice, a new tap always does.
  const sprungKey = `${ort.key}:${gesucht}`;
  const angesprungen = useRef<string | null>(null);
  const nachgeholt = useRef<string | null>(null);
  const liste = messages.data?.messages;
  useEffect(() => {
    if (angesprungen.current === sprungKey || !mitZiel) return;
    // **The wait for the first list is part of the jump** (1.103.0). From
    // the map or the playlist the chat opens with nothing in the cache, and
    // the jump waited for its first page without a sign. From use: "die
    // Karte bzw. die Playlist ohne Fortschrittsbalken angezeigt bis der
    // Sprung erfolgt". Measured: the sign stood in 20 of 39 samples.
    if (liste === undefined) {
      setLoadingTo(gesucht);
      return;
    }
    // The jump to the first unread gives way (see `mitZiel` above).
    gesprungen.current = true;

    if (liste.some((m) => m.id === gesucht)) {
      angesprungen.current = sprungKey;
      merkeSprung(`Sprung zu m=${gesucht}: steht im Verlauf`);
      springeZu(gesucht);
      return;
    }
    const neueste = liste[0]?.id ?? 0;
    if (gesucht > neueste) {
      if (nachgeholt.current !== sprungKey) {
        nachgeholt.current = sprungKey;
        merkeSprung(`Sprung zu m=${gesucht}: Liste war alt (neueste ${neueste}), neu geholt`);
        setLoadingTo(gesucht);
        void messages.refetch();
        return;
      }
      if (messages.isFetching) return;
      // **An inserted post has a high id and an old place** (#130): newer
      // than the newest by id, but not in the newest page. The jump loads
      // down to it like to any older post; one that is not this reader's
      // ends there with its own line.
      angesprungen.current = sprungKey;
      merkeSprung(`Sprung zu m=${gesucht}: nicht in der neuesten Seite, lade zurück`);
      springeZu(gesucht);
      return;
    }
    angesprungen.current = sprungKey;
    merkeSprung(`Sprung zu m=${gesucht}: älter als die geladene Seite, lade zurück`);
    springeZu(gesucht);
    // `springeZu` haengt am geladenen Verlauf und wird bei jedem Zeichnen neu
    // gebaut; der Zettel darueber sorgt dafuer, dass es trotzdem einmal bleibt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sprungKey, liste, messages.isFetching]);

  const newest = messages.data?.messages[0]?.id;
  const newestAt = messages.data?.messages[0]?.created_at;
  useEffect(() => {
    // Wer den Chat offen hat, hat gelesen. Ausgelöst wird das an der neuesten
    // Nachricht, nicht bei jedem Rendern -- sonst schriebe jedes Scrollen.
    //
    // Not in the draft (#85): nobody has anything to read there. And read
    // is what stands here, up to its time (1.79.0, `useMarkRead`) -- not
    // whatever the server has by the time the call arrives.
    if (newest !== undefined && newestAt !== undefined && !entwurf) markRead.mutate(newestAt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, withUser, newest]);

  return {
    /** An den Scrollkasten des Verlaufs -- die Seite hängt ihn ans `<div>`. */
    verlauf,
    verlaufScrollt,
    nachUntenSpringen,
    springeZu,
    ersteUngelesene,
    kanteAufgegeben,
  };
}
